import { ScrollView } from "react-native";
import type { Href } from "expo-router";
import { StyleSheet } from "react-native-unistyles";
import { Text, View } from "@src/components";
import { TenantLink } from "@src/features/tenants";
import { activationCameraKey, type CursorPager, type IncidentSummary } from "@visioni/a3s-monitoring/app";
import { EMPTY, formatInstant } from "../format";
import { CursorPagerBar, countOf } from "./CursorPagerBar";

interface IncidentsTableProps {
  readonly items: readonly IncidentSummary[];
  /** Dim the rows while they are the previous page's, held until the next arrives. */
  readonly isStale: boolean;
  readonly hrefFor: (incident: IncidentSummary) => Href;
  /** Adds an Organisation column, for a list spanning tenants. */
  readonly tenantLabel?: (incident: IncidentSummary) => string;
  readonly siteLabel: (siteId: string) => string;
  readonly cameraLabel: (cameraKey: string) => string;
  /** The zone the times are shown in — the one the list is filtered in. */
  readonly timeZone: string;
  readonly pager: CursorPager;
  readonly nextCursor: string | null;
  readonly isFetching: boolean;
}

const COLUMNS = [
  { label: "Started", style: "started" },
  { label: "Client", style: "client" },
  { label: "Calls", style: "count" },
  { label: "Activations", style: "count" },
  { label: "Police", style: "police" },
  { label: "Status", style: "status" },
  { label: "Organisation", style: "tenant" },
  { label: "Site", style: "site" },
  { label: "Camera", style: "camera" },
] as const;

export function IncidentsTable({
  items,
  isStale,
  hrefFor,
  tenantLabel,
  siteLabel,
  cameraLabel,
  timeZone,
  pager,
  nextCursor,
  isFetching,
}: IncidentsTableProps) {
  const columns = tenantLabel === undefined ? COLUMNS.filter((c) => c.label !== "Organisation") : COLUMNS;

  return (
    <View flex={1}>
      <ScrollView style={styles.scroll} contentContainerStyle={styles.scrollContent}>
        <View style={[styles.card, isStale && styles.stale]}>
          <View style={[styles.row, styles.headerRow]}>
            {columns.map((column) => (
              <Text key={column.label} textStyle="xs/semibold" color="textMuted" style={styles[column.style]}>
                {column.label}
              </Text>
            ))}
          </View>
          {items.map((incident) => {
            const cameraKey = activationCameraKey(incident);
            return (
              <TenantLink key={incident.id} href={hrefFor(incident)} style={styles.link}>
                {({ hovered }) => (
                  <View style={[styles.row, hovered && styles.hovered]}>
                    <Text textStyle="xs/regular" color="text" style={styles.started}>
                      {formatInstant(incident.startedAt, timeZone)}
                    </Text>
                    <Text textStyle="sm/semibold" color="text" style={styles.client} numberOfLines={1}>
                      {incident.clientNo}
                    </Text>
                    <Text textStyle="xs/regular" color="text" style={styles.count}>
                      {String(incident.callCount)}
                    </Text>
                    <Text textStyle="xs/regular" color="text" style={styles.count}>
                      {String(incident.activationCount)}
                    </Text>
                    <Text textStyle="xs/regular" color="text" style={styles.police}>
                      {incident.policeCalled ? "Called" : EMPTY}
                    </Text>
                    <Text textStyle="xs/regular" color="text" style={styles.status}>
                      {incident.settled ? "Settled" : "Open"}
                    </Text>
                    {tenantLabel !== undefined && (
                      <Text textStyle="xs/regular" color="text" style={styles.tenant} numberOfLines={1}>
                        {tenantLabel(incident)}
                      </Text>
                    )}
                    <Text textStyle="xs/regular" color="text" style={styles.site} numberOfLines={1}>
                      {incident.a3sSite !== undefined ? siteLabel(incident.a3sSite) : EMPTY}
                    </Text>
                    <Text textStyle="xs/regular" color="text" style={styles.camera} numberOfLines={1}>
                      {cameraKey !== undefined ? cameraLabel(cameraKey) : EMPTY}
                    </Text>
                  </View>
                )}
              </TenantLink>
            );
          })}
          {items.length === 0 && (
            <View style={styles.row}>
              <Text textStyle="sm/regular" color="textMuted">
                {
                  nextCursor === null ?
                    "No more incidents"
                    // The fleet list walks a bounded number of months per page.
                  : "No incidents in the months this page covered — older ones may follow"
                }
              </Text>
            </View>
          )}
        </View>
      </ScrollView>
      <CursorPagerBar
        pager={pager}
        nextCursor={nextCursor}
        isFetching={isFetching}
        summary={countOf(items.length, "incident")}
      />
    </View>
  );
}

const styles = StyleSheet.create((theme) => ({
  scroll: { flex: 1 },
  scrollContent: { padding: theme.spacing.xl },
  card: {
    backgroundColor: theme.colors.surfaceCard,
    borderWidth: 1,
    borderColor: theme.colors.borderCard,
    borderRadius: theme.spacing.xl,
    overflow: "hidden",
  },
  stale: { opacity: 0.5 },
  link: { cursor: "pointer" },
  row: {
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing.lg,
    paddingHorizontal: theme.spacing.xl,
    paddingVertical: theme.spacing.lg,
    borderTopWidth: 1,
    borderColor: theme.colors.borderCard,
  },
  headerRow: { borderTopWidth: 0, backgroundColor: theme.colors.surfaceCardInner },
  hovered: { backgroundColor: theme.colors.surfaceCardInner },
  started: { width: 180 },
  client: { width: 120 },
  count: { width: 80, textAlign: "right" },
  police: { width: 70 },
  status: { width: 70 },
  tenant: { flex: 1, minWidth: 120 },
  site: { flex: 1, minWidth: 100 },
  camera: { flex: 1, minWidth: 100 },
}));
